import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LANGS } from "@/lib/i18n";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { LogOut } from "lucide-react";
import { ChangePasswordCard } from "@/components/ChangePasswordCard";

export default function Settings() {
  const { user, setUser, signOut } = useAuth();
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    company: user?.company || "",
    language: user?.language || lang,
  });
  const [saving, setSaving] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", form);
      setUser(data);
      setLang(form.language);
      toast.success(t(lang, "settings.saved"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <AppShell>
      <div className="editorial-rule pt-4">
        <h1 className="font-display text-4xl">{t(lang,"settings.title")}</h1>
      </div>
      <div className="mt-8 max-w-xl bg-white border border-[#E9ECEF] p-6">
        <h2 className="text-xs tracking-[0.24em] uppercase text-[#868E96]">{t(lang,"settings.profile")}</h2>
        <form onSubmit={save} className="mt-4 space-y-4">
          <div>
            <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"auth.name")}</label>
            <input data-testid="settings-name" value={form.name} onChange={onChange("name")} className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
          </div>
          <div>
            <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"auth.company")}</label>
            <input data-testid="settings-company" value={form.company} onChange={onChange("company")} className="mt-2 w-full border border-[#E9ECEF] bg-[#F8F9FA] px-4 py-3 focus:outline-none focus:border-[#0A1128]" />
          </div>
          <div>
            <label className="text-xs tracking-[0.18em] uppercase text-[#868E96]">{t(lang,"settings.language")}</label>
            <div className="mt-2 flex gap-2">
              {LANGS.map((l) => (
                <button
                  type="button"
                  key={l.code}
                  onClick={() => setForm({ ...form, language: l.code })}
                  data-testid={`settings-lang-${l.code}`}
                  className={`px-4 py-2 text-sm border ${form.language === l.code ? "bg-[#0A1128] text-white border-[#0A1128]" : "border-[#E9ECEF] text-[#0A1128] hover:bg-[#F8F9FA]"}`}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving} className="bg-[#0A1128] text-white px-6 py-3 hover:bg-[#1C2538] disabled:opacity-60" data-testid="settings-save-btn">
            {saving ? t(lang,"common.loading") : t(lang,"settings.save")}
          </button>
        </form>
      </div>

      {user?.auth_provider === "password" && <ChangePasswordCard />}

      <div className="mt-8 max-w-xl">
        <button
          onClick={async () => { await signOut(); navigate("/"); }}
          className="inline-flex items-center gap-2 border border-[#0A1128] px-4 py-2 hover:bg-white"
          data-testid="settings-signout-btn"
        >
          <LogOut className="w-4 h-4" /> {t(lang,"settings.signout")}
        </button>
      </div>
    </AppShell>
  );
}
